import { ASC, DESC } from 'framework/list/baseListHelper';
import { VirtualTableRowType } from './types';
import type {
  ListTableAction,
  ListTableGroup,
  ListTableGroupLoading,
  ListTableRow,
  ListTableSort,
  VirtualTableRow,
  VirtualTableWindow,
} from './types';

export const getVisibleActions = <Row extends ListTableRow>(actions: ListTableAction<Row>[], row: Row) =>
  actions.filter(action => action.isVisible?.(row) !== false);

export const getNextSort = (columnId: string, currentSort: ListTableSort | null): ListTableSort => ({
  columnId,
  direction: currentSort?.columnId === columnId && currentSort.direction === ASC ? DESC : ASC,
});

export const isGroupExpanded = <Row extends ListTableRow>(
  group: ListTableGroup<Row>,
  expandedGroups: Record<string, boolean>,
) => group.isExpanded ?? expandedGroups[String(group.id)] ?? group.defaultExpanded ?? true;

export const getVirtualRows = <Row extends ListTableRow>({
  groups,
  rows,
  loading,
  expandedGroups,
  withChildren = false,
  childrenSource = 'children',
  expandedRows = {},
}: {
  groups?: ListTableGroup<Row>[];
  rows?: Row[];
  loading?: ListTableGroupLoading;
  expandedGroups: Record<string, boolean>;
  withChildren?: boolean;
  childrenSource?: string;
  expandedRows?: Record<string, boolean>;
}): VirtualTableRow<Row>[] => {
  const toVirtualRows = (data: Row[], isGrouped: boolean, parentPath: string[], depth = 0): VirtualTableRow<Row>[] =>
    data.flatMap(row => {
      if (!withChildren) return [{ type: VirtualTableRowType.DATA, row, isGrouped }];

      const value = (row as unknown as Record<string, unknown>)[childrenSource];
      const children = Array.isArray(value) ? (value as Row[]) : [];
      const path = [...parentPath, String(row.id)];
      const key = JSON.stringify(path);
      const isExpanded = expandedRows[key] ?? false;
      const treeRow: VirtualTableRow<Row> = {
        type: VirtualTableRowType.CHILDREN,
        row,
        isGrouped,
        depth,
        childCount: children.length,
        isExpanded,
        key,
      };

      return isExpanded ? [treeRow, ...toVirtualRows(children, isGrouped, path, depth + 1)] : [treeRow];
    });

  if (groups) {
    return groups.flatMap(group => {
      const isExpanded = isGroupExpanded(group, expandedGroups);
      const groupRow: VirtualTableRow<Row> = { type: VirtualTableRowType.GROUP, group, isExpanded };
      if (!isExpanded) return [groupRow];

      const dataRows = toVirtualRows(group.rows, true, ['group', String(group.id)]);
      const groupLoading = group.listTableGroupLoading;
      const tailRows: VirtualTableRow<Row>[] = groupLoading
        ? [{ type: VirtualTableRowType.TAIL, group, loading: groupLoading }]
        : [];

      return [groupRow, ...dataRows, ...tailRows];
    });
  }

  const dataRows = toVirtualRows(rows ?? [], false, ['rows']);
  const tailRows: VirtualTableRow<Row>[] = loading ? [{ type: VirtualTableRowType.TAIL, loading }] : [];

  return [...dataRows, ...tailRows];
};

export const getVirtualTableWindow = <Row extends ListTableRow>({
  rows,
  scrollTop,
  virtualHeight,
  rowHeight,
  groupRowHeight,
  overscan,
}: {
  rows: VirtualTableRow<Row>[];
  scrollTop: number;
  virtualHeight: number;
  rowHeight: number;
  groupRowHeight: number;
  overscan: number;
}): VirtualTableWindow<Row> => {
  let totalHeight = 0;
  const rowOffsets = rows.map(row => {
    const offset = totalHeight;
    totalHeight += row.type === VirtualTableRowType.GROUP ? groupRowHeight : rowHeight;
    return offset;
  });
  const firstVisibleIndex = rowOffsets.findIndex((offset, index) => {
    const height = rows[index].type === VirtualTableRowType.GROUP ? groupRowHeight : rowHeight;
    return offset + height > scrollTop;
  });
  const startIndex = Math.max(0, (firstVisibleIndex === -1 ? 0 : firstVisibleIndex) - overscan);
  const endIndex = Math.min(
    rows.length,
    Math.ceil((scrollTop + virtualHeight) / Math.min(rowHeight, groupRowHeight)) + overscan,
  );

  return {
    visibleRows: rows.slice(startIndex, endIndex),
    startIndex,
    topSpacerHeight: rowOffsets[startIndex] ?? 0,
    bottomSpacerHeight: Math.max(0, totalHeight - (rowOffsets[endIndex] ?? totalHeight)),
  };
};
